import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { toast } from 'sonner';
import * as Sentry from '@sentry/browser';
import { Check, ChevronDown, Download, FileSpreadsheet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SprintEntity, SprintProductStatus, useSprintProductsForViewer, WorkDayProduct } from '@data';
import { fetchSprintProductReports } from '@data/api';
import { transformSprintProductReportsData } from '@data/api/utils/helpers';
import { useOrganizationChapters } from '@data/hooks/organization';
import { limitConcurrency } from '@app/utils/helpers/limitConcurrency';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/ui/popover';
import { Button } from '@ui/components/ui/button';
import SpreadsheetPreview from '@app/components/core/SpreadsheetPreview';
import { getFullNameOrEmail, formatDateString, toFixedHours } from '@app/utils';
import { Language } from '@app/constants/enums';
import { useIsInternalReader } from '../internalReader';

// Descriptions are stored as HTML; flatten to plain text (collapse whitespace) for spreadsheet cells.
const stripHtml = (html: string): string => {
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim();
};

// One read per sprint, so the count is the product's whole history — 80 on a product two years in, all
// fired together, against a database that has 22 connections to give. Four at a time keeps it standing.
const READS_AT_ONCE = 4;

// Two sheets: every time entry, then a per-person roll-up per sprint. The prototype carries a rate and a
// cost column on both sheets; those are the financial surface this epic does not ship.
const ENTRY_COLUMNS = ['Sprint', 'Date', 'Name', 'Role', 'Hours', 'Description'];
const SUMMARY_COLUMNS = ['Sprint', 'Name', 'Role', 'Hours'];
const round2 = (value: number): number => Math.round(value * 100) / 100;

/** One cell of the workbook, in the shape `write-excel-file` takes. */
type SheetCell = { value: string; fontWeight?: 'bold' } | { value: number; type: NumberConstructor } | null;

type SheetRow = SheetCell[];

/**
 * A sheet's cells: the headings in bold on row one, then the rows.
 *
 * Insights writes every cell as text so Excel cannot re-read a value as something else. Hours go out as
 * numbers here instead, because the whole point of this export is that the client sums and pivots them —
 * a column of text hours cannot be totalled. Everything else stays text for Insights' reason.
 */
const toSheetData = (columns: string[], rows: (string | number)[][]): SheetRow[] => [
  columns.map(heading => ({ value: heading, fontWeight: 'bold' as const })),
  ...rows.map<SheetRow>(row =>
    row.map<SheetCell>(value => {
      if (typeof value === 'number') return { value, type: Number };
      return value === '' ? null : { value };
    }),
  ),
];

type ExportSprint = { id: string; number: number; startDate: string; endDate: string };

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId?: string;
  productId?: string;
  // Pre-computed sprint list. When supplied (e.g. the PM product page, which can't read the
  // client-scoped endpoint), it's used verbatim instead of deriving from the viewer's own list.
  sprints?: ExportSprint[];
}

// Sprint export for a client's product. Pick sprints from a checkbox dropdown (nothing selected at
// first); once something's chosen the resulting spreadsheet shows as a live preview (the same
// SpreadsheetPreview the Insights export uses), then downloads as an .xlsx written by
// write-excel-file. Two sheets: "Sprints" is one row per time entry (Sprint · Date · Name · Role ·
// Hours · Description), "Hours summary" totals hours per person per sprint.
const ExportDialog = ({
  open,
  onOpenChange,
  organizationId,
  productId,
  sprints: sprintsProp,
}: ExportDialogProps): JSX.Element => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  // Detailed per-day reports per sprint, fetched when the dialog opens.
  const [detailBySprint, setDetailBySprint] = useState<Record<string, WorkDayProduct[]>>({});
  const [loading, setLoading] = useState(false);

  const statuses = [SprintProductStatus.closed, SprintProductStatus.approved, SprintProductStatus.invoiced];
  // Held, not called twice: the same answer decides what the list reads and what the file is filled from.
  const internal = useIsInternalReader();
  const { data: sprintProducts } = useSprintProductsForViewer(internal, organizationId, {
    statuses,
    productId,
  });
  const { data: chapters } = useOrganizationChapters(organizationId, { includeDisabled: true });
  const roleLabel = (key: string): string => chapters?.items.find(chapter => chapter.role === key)?.text ?? key;

  // This product's sprints, newest first. A caller-supplied list wins over the client-scoped fetch.
  const derivedSprints = useMemo(
    () =>
      (sprintProducts?.items ?? [])
        .filter(item => item.product?.id === productId && item.sprint)
        .map(item => item.sprint)
        // Newest by start date: sprint numbers restart each year, so they don't order across years.
        .sort(
          (a, b) =>
            new Date(b.startDate ?? 0).getTime() - new Date(a.startDate ?? 0).getTime() ||
            (b.number ?? 0) - (a.number ?? 0),
        ),
    [sprintProducts, productId],
  );
  const sprints: ExportSprint[] = sprintsProp ?? derivedSprints;

  // Reset the picker each time the dialog opens — nothing selected, so the placeholder shows.
  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setPickerOpen(false);
    }
  }, [open]);

  // Fetch every sprint's detailed reports once the dialog is open; we keep the raw per-day data and
  // flatten to rows below, so toggling the selection re-slices without re-fetching.
  useEffect(() => {
    if (!open || sprints.length === 0) return undefined;
    let cancelled = false;
    setLoading(true);
    const queue = limitConcurrency(READS_AT_ONCE);
    Promise.all(
      sprints.map(sprint =>
        queue(async () => {
          // The report helpers only read id/startDate/endDate; a lightweight sprint satisfies them.
          const sprintEntity = sprint as SprintEntity;
          const reports = await fetchSprintProductReports({
            sprint: sprintEntity,
            productId: productId as string,
            chapters: undefined,
            // The file has to be the sprints as they were published, not what has been logged since.
            asClient: internal,
          });
          return [sprint.id, transformSprintProductReportsData(false, sprintEntity, reports).detailed ?? []] as const;
        }),
      ),
    )
      .then(entries => {
        if (cancelled) return;
        setDetailBySprint(Object.fromEntries(entries));
        setLoading(false);
      })
      .catch(error => {
        if (cancelled) return;
        setLoading(false);
        // Its own message, not the download one: nothing has been written yet, so telling the client the
        // file could not be written names a step that never ran.
        Sentry.captureException(error);
        toast.error(t('exportReadFailed'));
      });
    return () => {
      cancelled = true;
    };
    // `t` is only read on the failure path; re-running the fan-out on a language change would be worse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sprints, productId, internal]);

  const toggleSprint = (id: string): void =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const chosen = useMemo(() => sprints.filter(sprint => selected.has(sprint.id)), [selected, sprints]);

  const allSelected = sprints.length > 0 && selected.size === sprints.length;
  const toggleAll = (): void =>
    setSelected(prev => (prev.size === sprints.length ? new Set() : new Set(sprints.map(sprint => sprint.id))));

  const range = (start?: string, end?: string): string =>
    start && end
      ? `${formatDateString(start, language as Language, 'dd MMM')} – ${formatDateString(
          end,
          language as Language,
          'dd MMM yyyy',
        )}`
      : '';

  const pickerLabel = ((): string => {
    if (selected.size === 0) return t('selectSprints');
    if (allSelected) return t('allSprintsCount', { count: sprints.length });
    return t('nOfTotalSelected', { count: selected.size, total: sprints.length });
  })();

  // Flatten the chosen sprints' days → roles → people into one row per entry, and roll the same walk up
  // into per-sprint × person × role totals.
  const { entryRows, summaryRows } = useMemo(() => {
    const entries: (string | number)[][] = [];
    const totals = new Map<string, { sprint: string; number: number; name: string; role: string; hours: number }>();
    chosen.forEach(sprint =>
      (detailBySprint[sprint.id] ?? [])
        .filter(day => day.totalMinutes > 0)
        .forEach(day =>
          day.chapters
            .filter(chapter => chapter.totalMinutes > 0)
            .forEach(chapter => {
              const role = roleLabel(chapter.name);
              chapter.reports.forEach(report => {
                const sprintLabel = `${t('sprint')} ${sprint.number}`;
                const name = getFullNameOrEmail(report.involvement.membership.user);
                const hours = Number(toFixedHours(report.minutes / 60));
                entries.push([sprintLabel, day.date, name, role, hours, stripHtml(report.today ?? '')]);
                const key = `${sprint.id}|${report.involvement.membership.id}|${chapter.name}`;
                const total = totals.get(key) ?? {
                  sprint: sprintLabel,
                  number: sprint.number,
                  name,
                  role,
                  hours: 0,
                };
                total.hours += report.minutes / 60;
                totals.set(key, total);
              });
            }),
        ),
    );
    // Most hours first per sprint, so the client reads the biggest contributions top-down.
    const summary: (string | number)[][] = [...totals.values()]
      .sort((a, b) => b.number - a.number || b.hours - a.hours)
      .map(total => [total.sprint, total.name, total.role, Number(toFixedHours(total.hours))]);
    if (summary.length > 0) {
      const hours = summary.reduce((sum, row) => sum + (row[3] as number), 0);
      summary.push(['Total', '', '', round2(hours)]);
    }
    return { entryRows: entries, summaryRows: summary };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen, detailBySprint, chapters, language, t, productId]);

  const filename = chosen.length === 1 ? `${t('sprint')} ${chosen[0].number}.xlsx` : 'Sprints.xlsx';
  const sheets = useMemo(
    () => [
      { name: 'Sprints', columns: ENTRY_COLUMNS, rows: entryRows },
      { name: 'Hours summary', columns: SUMMARY_COLUMNS, rows: summaryRows },
    ],
    [entryRows, summaryRows],
  );

  // Column widths per sheet, in the order the columns are declared above.
  const COLUMN_WIDTHS: Record<string, number[]> = {
    Sprints: [10, 12, 24, 22, 8, 80],
    'Hours summary': [10, 24, 22, 8],
  };

  // Imported where it is used, so its chunk is fetched by the clients who export rather than by everyone
  // who opens a product. Same writer and the same failure handling as the Insights export.
  const exportXlsx = async (): Promise<void> => {
    try {
      const { default: writeXlsxFile } = await import('write-excel-file/browser');
      await writeXlsxFile(
        sheets.map(sheet => ({
          data: toSheetData(sheet.columns, sheet.rows),
          sheet: sheet.name,
          columns: COLUMN_WIDTHS[sheet.name].map(width => ({ width })),
        })),
      ).toFile(filename);
      toast.success(t('sprintsExported'));
    } catch (error) {
      // Never a silent failure: the client is standing at a download that did not happen, and a writer
      // that throws on real data is something we want to hear about rather than only they do.
      Sentry.captureException(error);
      toast.error(t('exportFailed'));
    }
  };

  // `key` is a parameter rather than left to the caller: the sprint rows are rendered from a map, and a
  // helper returning bare JSX gives React no key to place them by.
  const checkboxRow = (
    key: string,
    label: string,
    checked: boolean,
    onClick: () => void,
    hint?: string,
  ): JSX.Element => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted/60"
    >
      <span
        className={cx(
          'flex size-4 shrink-0 items-center justify-center rounded border transition-colors',
          checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
        )}
      >
        {checked && <Check className="size-3" />}
      </span>
      <span className="min-w-0 flex-1 truncate font-medium text-foreground">{label}</span>
      {hint && <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{hint}</span>}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('actions.exportSprints')}</DialogTitle>
        </DialogHeader>

        {/* Sprint picker — a dropdown with checkboxes (plus a "select all"). */}
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger
            type="button"
            className="flex w-72 items-center justify-between gap-2 rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className={cx('truncate', selected.size === 0 && 'text-muted-foreground')}>{pickerLabel}</span>
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-1">
            {checkboxRow('all', t('allSprintsCount', { count: sprints.length }), allSelected, toggleAll)}
            <div className="my-1 border-t border-border" />
            <div className="max-h-56 overflow-y-auto pr-0.5">
              {sprints.map(sprint =>
                checkboxRow(
                  sprint.id,
                  `${t('sprint')} ${sprint.number}`,
                  selected.has(sprint.id),
                  () => toggleSprint(sprint.id),
                  range(sprint.startDate, sprint.endDate),
                ),
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Fixed-height area so the dialog never resizes: a placeholder that fades out while the live
            preview rolls down over it (grid-rows reveal) once a sprint is chosen. */}
        <div className="relative h-[348px]">
          <div
            className={cx(
              'absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border px-6 text-center transition-opacity duration-300',
              selected.size > 0 && 'pointer-events-none opacity-0',
            )}
          >
            <FileSpreadsheet className="size-8 text-muted-foreground/50" aria-hidden />
            <p className="text-sm text-muted-foreground">{t('pickSprintsToPreview')}</p>
          </div>
          <div
            className={cx(
              'absolute inset-x-0 top-0 grid transition-[grid-template-rows] duration-[400ms] ease-out',
              selected.size > 0 ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
            )}
          >
            <div className="min-h-0 overflow-hidden">
              {/* Shorter grid than the container: the sheet-tab bar takes the remaining ~35px. */}
              <SpreadsheetPreview filename={filename} sheets={sheets} loading={loading} heightClass="h-[266px]" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button disabled={loading || selected.size === 0 || entryRows.length === 0} onClick={exportXlsx}>
            <Download className="size-4" />
            {chosen.length > 1 ? t('exportNSprints', { count: chosen.length }) : t('actions.export')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDialog;
